import { useParams } from "react-router";
import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { DfyOrderStatus, type DfyOrderSort } from "@repo/api/dfy";
import { usePagedDfyEditorOrdersQuery } from "@/lib/queries/dfy-queries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { paths } from "@/App";
import AppHeader from "@/lib/components/AppHeader";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { Input } from "@/lib/components/Input";
import DfyOrdersList, { dfyStatusLabel } from "./DfyOrdersList";
import { IconExclamationCircle, IconSearch } from "@tabler/icons-react";

// Done-for-You editor team order list. POC: whole view in one file.
export default function DfyEditorOrdersPage() {
  const { teamExternalId } = useParams();

  const [status, setStatus] = useState<DfyOrderStatus | "all">("all");
  const [sort, setSort] = useState<DfyOrderSort>("newest");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  const { ref, inView } = useInView();
  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
  } = usePagedDfyEditorOrdersQuery(
    teamExternalId!,
    sort,
    status === "all" ? undefined : status,
    debouncedSearch || undefined,
  );

  useEffect(() => {
    if (inView && !isFetching && !isError && hasNextPage) {
      void fetchNextPage();
    }
  }, [inView, isFetching, isError, hasNextPage, fetchNextPage]);

  if (!teamExternalId) {
    return null;
  }

  const orders = data?.pages.flatMap((page) => page.orders) ?? [];

  return (
    <div className="flex flex-1 flex-col overflow-y-auto">
      <AppHeader title="Orders" className="border-b border-border" />

      <DfyOrderFilters
        status={status}
        onStatusChange={setStatus}
        sort={sort}
        onSortChange={setSort}
        search={search}
        onSearchChange={setSearch}
      />

      <div className="flex flex-1 flex-col">
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : isError && !data ? (
          <div
            role="alert"
            className="flex flex-1 items-center justify-center gap-2 py-12"
          >
            <IconExclamationCircle
              size={24}
              className="shrink-0 text-error"
              aria-hidden="true"
            />
            <p className="font-medium">Failed to load orders</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-12">
            <p className="text-sm text-text-secondary">
              {debouncedSearch
                ? "No orders match your search."
                : status === "all"
                  ? "No orders yet."
                  : "No orders with this status."}
            </p>
          </div>
        ) : (
          <DfyOrdersList
            orders={orders}
            orderHref={(order) =>
              paths.dfyEditorOrder(teamExternalId, order.externalId)
            }
          />
        )}
        {isError && data && !isFetchNextPageError && (
          <div
            role="alert"
            className="flex items-center justify-center gap-2 py-12"
          >
            <IconExclamationCircle
              size={24}
              className="shrink-0 text-error"
              aria-hidden="true"
            />
            <p className="font-medium">Failed to refresh orders</p>
          </div>
        )}
        <div ref={ref} className="flex min-h-12 items-center justify-center">
          {isFetchingNextPage ? (
            <LoadingSpinner size={24} />
          ) : isFetchNextPageError ? (
            <div
              role="alert"
              className="flex items-center justify-center gap-2 py-12"
            >
              <IconExclamationCircle
                size={24}
                className="shrink-0 text-error"
                aria-hidden="true"
              />
              <p className="font-medium">Failed to load more orders</p>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function DfyOrderFilters({
  status,
  onStatusChange,
  sort,
  onSortChange,
  search,
  onSearchChange,
}: {
  status: DfyOrderStatus | "all";
  onStatusChange: (status: DfyOrderStatus | "all") => void;
  sort: DfyOrderSort;
  onSortChange: (sort: DfyOrderSort) => void;
  search: string;
  onSearchChange: (search: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-5 border-b bg-new-foreground-secondary px-4 py-3 md:px-6">
      <div className="mr-auto h-9 w-full max-w-full md:w-92">
        <Input
          containerSize="sm"
          placeholder="Search by order ID & email"
          aria-label="Search orders"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          leadingComponent={<IconSearch size={18} />}
          className="border-grey-150 bg-white"
        />
      </div>
      <Select
        value={status}
        onValueChange={(value) =>
          onStatusChange(value as DfyOrderStatus | "all")
        }
      >
        <SelectTrigger
          variant="secondary"
          className="h-fit w-auto shrink-0 py-2"
          aria-label="Filter orders by status"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectItem value="all">All statuses</SelectItem>
          {DfyOrderStatus.options.map((value) => (
            <SelectItem key={value} value={value}>
              {dfyStatusLabel(value)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        value={sort}
        onValueChange={(value) => onSortChange(value as DfyOrderSort)}
      >
        <SelectTrigger
          variant="secondary"
          className="h-fit w-auto shrink-0 py-2"
          aria-label="Sort orders by creation date"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectItem value="newest">Newest first</SelectItem>
          <SelectItem value="oldest">Oldest first</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
